import { asUnknown } from '../utils/common';
import { isDefined } from '../utils/guards';
import { merge, objectEntries } from '../utils/object';
import { sortBy } from '../utils/sort';

/**
 * Enumeration data object type
 * @export
 */
export type EnumDataObject<
	Data extends unknown,
	IdValue extends number,
	IdKey extends string,
	TExtraDataExtended extends object | undefined = undefined,
> = { [key in IdKey]: IdValue } & EnumExtraDataWithExtended<Data, TExtraDataExtended> & EnumDataHelpers;

/**
 * Enumeration data helpers type
 * @export
 */
export type EnumDataHelpers = {
	getI18nKey: (i18nKey: 'label' | 'description' | 'helperText', i18nOptions?: Record<string, unknown>) => string;
	getI18n: (t: RawTFunction, i18nKey: 'label' | 'description' | 'helperText', i18nOptions?: Record<string, unknown>) => string | null | undefined;
	getLabel: (t: RawTFunction, i18nOptions?: Record<string, unknown>) => string;
	displayOrder: number;
};
type RawTFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Keyof enum data object type
 * @export
 */
export type KeyofEnumData<
	Data extends unknown,
	IdValue extends number,
	IdKey extends string,
	TExtraDataExtended extends object | undefined,
	V extends Data[keyof Data] | unknown,
> = keyof EnumDataObject<Data, IdValue, IdKey, TExtraDataExtended> | ((item: Data, value: V) => boolean);

export type EnumExtraDataWithExtended<TExtraData extends unknown, TExtraDataExtended extends object | undefined> = TExtraData &
	(TExtraDataExtended extends undefined ? {} : TExtraDataExtended);

export type ExtraDataWithExtendedInput<IdValue extends number, TExtraDataExtended extends object | undefined> = TExtraDataExtended extends undefined
	? null
	: Record<IdValue, TExtraDataExtended>;

/**
 * Enumeration base helper class
 * @author filipditrich <ditrich@nfctron.com>
 */
export class EnumHelperBase<
	ExtraData extends object,
	IdValue extends number = number,
	Name extends string = string,
	IdKey extends string = `${Name}Id`,
	TExtraDataExtended extends object | undefined = undefined,
> {
	public readonly enumName: Name;
	public readonly idKey: IdKey;
	public readonly data: Record<IdValue, ExtraData & Partial<EnumDataHelpers>>;
	public readonly dataExtended: ExtraDataWithExtendedInput<IdValue, TExtraDataExtended>;
	public list: EnumDataObject<ExtraData, IdValue, IdKey, TExtraDataExtended>[];

	private readonly _customThreshold: number;
	private readonly _getI18nKeyFactory: (
		id: IdValue,
		data: EnumExtraDataWithExtended<ExtraData, TExtraDataExtended>,
	) => (i18nKey: 'label' | 'description' | 'helperText', i18nOptions?: Record<string, unknown>) => string;

	public extendWith = <TAnother extends object>(
		data: Record<IdValue, TAnother>,
	): ExtraDataWithExtendedInput<IdValue, TExtraDataExtended & TAnother> =>
		asUnknown<ExtraDataWithExtendedInput<IdValue, TExtraDataExtended & TAnother>>(merge(this.dataExtended ?? {}, data));

	constructor(
		enumName: Name,
		data: Record<IdValue, ExtraData & Partial<EnumDataHelpers>>,
		idKey: IdKey = `${enumName}Id` as IdKey,
		extendedData: ExtraDataWithExtendedInput<IdValue, TExtraDataExtended>,
		options?: {
			customThreshold?: number;
			getI18nKeyFactory?: (
				id: IdValue,
				data: EnumExtraDataWithExtended<ExtraData, TExtraDataExtended>,
			) => (i18nKey: 'label' | 'description' | 'helperText', i18nOptions?: Record<string, unknown>) => string;
		},
	) {
		this.data = data;
		this.dataExtended = extendedData;
		this.enumName = enumName;
		this._customThreshold = options?.customThreshold ?? 1000;
		this.idKey = idKey;
		this._getI18nKeyFactory = options?.getI18nKeyFactory ?? ((id) => (i18nKey) => `constants::${this.enumName}.${id}.${i18nKey}`);
		this.list = objectEntries(data)
			.map(([id, d]) => {
				/** id value */
				const idValue = Number(id) as IdValue;
				const inputData = asUnknown<any>({ ...d, ...extendedData?.[idValue] });
				/** helpers */
				const helpers: EnumDataHelpers = {
					getI18nKey: this._getI18nKeyFactory(idValue, inputData),
					getLabel: (t, i18nOptions) =>
						t(this._getI18nKeyFactory(idValue, inputData)('label', i18nOptions)) ?? this._getI18nKeyFactory(idValue, inputData)('label', i18nOptions),
					getI18n: (t, i18nKey, i18nOptions) => t(this._getI18nKeyFactory(idValue, inputData)(i18nKey, i18nOptions)),
					displayOrder: inputData?.displayOrder ?? idValue,
				};
				return {
					/** keyId: value */
					[this.idKey]: idValue,
					/** extra data */
					...inputData,
					/** helpers */
					...helpers,
				} as EnumDataObject<ExtraData, IdValue, IdKey, TExtraDataExtended>;
			})
			.sort(sortBy('displayOrder' as any, { direction: 'asc' }));
	}

	/**
	 * Returns enum value by its property
	 * @param value Value to search for
	 * @param prop Property to search by
	 * @returns Enum value
	 */
	get<V extends ExtraData[keyof ExtraData] | unknown = ExtraData[keyof ExtraData]>(
		value: V,
		prop: KeyofEnumData<ExtraData, IdValue, IdKey, TExtraDataExtended, V> = this.idKey,
	): EnumDataObject<ExtraData, IdValue, IdKey, TExtraDataExtended> {
		return this._getFactory(this.list)(value, prop);
	}

	/**
	 * Returns enum value by its property factory method
	 * @param list List of enum values
	 * @returns Getter factory to get enum value by its given property
	 */
	public _getFactory =
		(list: EnumDataObject<ExtraData, IdValue, IdKey, TExtraDataExtended>[]) =>
		/**
		 * Default getter method implementation
		 * @param value Value to search for
		 * @param prop Property to search by
		 * @returns Enum value
		 */ <V extends ExtraData[keyof ExtraData] | unknown = ExtraData[keyof ExtraData]>(
			value: V,
			prop: KeyofEnumData<ExtraData, IdValue, IdKey, TExtraDataExtended, V> = this.idKey,
		): EnumDataObject<ExtraData, IdValue, IdKey, TExtraDataExtended> => {
			const result = list.find((item) => {
				if (typeof prop === 'function') return prop(item, value);
				if (prop === this.idKey) return item[prop] === Number(value);
				return item[prop] === value;
			});
			if (!isDefined(result))
				throw new Error(`[EnumHelper] Could not find ${this.enumName} enumeration with "${prop?.toString()}" equal to "${value}".`);
			return result;
		};

	/**
	 * Returns whether the enum _mainKey value is a custom enum or a base (DB-defined) enum
	 * @param value Enum value
	 * @returns Whether the enum value is custom (via threshold comparison)
	 */
	isCustom(value: IdValue): boolean {
		return value > this._customThreshold;
	}

	/**
	 * Returns i18n key of the given enum value
	 * @param value Enum value
	 * @param i18nKey i18n key type
	 * @returns Translated i18n key
	 */
	getEnumI18nKey(value: IdValue, i18nKey: 'label' | 'description' | 'helperText'): string {
		const data = asUnknown<any>({ ...this.data[value], ...this.dataExtended?.[value] });
		return this._getI18nKeyFactory(value, data)(i18nKey);
	}

	/**
	 * Returns list of enum values
	 * @returns List of enum values
	 */
	public getValues(): ReadonlyArray<IdValue> {
		return Object.keys(this.data).map((key) => Number(key) as IdValue);
	}
}
